import { Loader2 } from "lucide-react";
import type { UploadBatch, UploadState } from "@/features/project/documents-api";
import { BrainUploadRow } from "./brain-item-row";

/**
 * The files being sent from this screen, at the top of the Sources tab
 * (2026-10-03, the owner).
 *
 * Until then the rows sat under the shelves, so a person who picked a file saw
 * nothing happen, and a refused file's reason was out of sight. Now one line
 * says how far the sending is, "Uploading 3 of 7 documents", and the files
 * that failed or were refused come first, each with its reason. The line goes
 * when every file has settled; a filed file is then the organising's to say.
 */
export function UploadStatus({
  uploads,
  batch,
  onRetry,
  onDismiss,
}: {
  uploads: readonly UploadState[];
  batch: UploadBatch;
  onRetry: (key: string) => void;
  onDismiss: (key: string) => void;
}) {
  const going = batch.settled < batch.total;
  if (!going && uploads.length === 0) return null;

  // Failed and refused first: those need a person; the rest only need time.
  const rows = [
    ...uploads.filter((upload) => upload.status === "error"),
    ...uploads.filter((upload) => upload.status !== "error"),
  ];

  return (
    <div className="mb-4 flex flex-col gap-2">
      {going && (
        <section
          aria-label="Uploading"
          className="rounded-card-lg border-[1.5px] border-border bg-surface px-5 py-3"
        >
          <p className="flex items-center gap-2 text-[0.86rem] text-ink" role="status" aria-live="polite">
            <Loader2 size={15} aria-hidden className="shrink-0 animate-spin text-muted" />
            {uploadingWords(batch)}
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-subtle">
            <div
              className="h-full rounded-full bg-brand transition-[width]"
              style={{ width: `${Math.round((batch.settled / batch.total) * 100)}%` }}
            />
          </div>
        </section>
      )}
      {rows.length > 0 && (
        <ul className="overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface">
          {rows.map((upload) => (
            <BrainUploadRow
              key={upload.key}
              upload={upload}
              onRetry={() => onRetry(upload.key)}
              onDismiss={() => onDismiss(upload.key)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** "Uploading 1 of 1 document.", "Uploading 3 of 7 documents." */
export function uploadingWords({ total, settled }: UploadBatch): string {
  const at = Math.min(settled + 1, total);
  return `Uploading ${at} of ${total} ${total === 1 ? "document" : "documents"}.`;
}
